import { Layer } from "@deck.gl/core";
import { GeoJsonLayer, IconLayer } from "@deck.gl/layers";
import type { AxiosResponse } from "axios";
import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import type { FeatureCollection } from "geojson";

import MarkerIcon from "@/assets/images/map-pin.png";
import {
  api,
  type StaticMapOverlaysOverlay,
  type StaticMapOverlaysOverlayConfiguration,
} from "@/lib/api";
import { getRgbForStoredOverlayColor } from "@/features/shared/widgets/model-map/static-overlay-color-presets";

type ColorRGB = [red: number, green: number, blue: number];

export type StaticMapOverlayEntry = {
  instanceId: string;
  label: string;
  /** From widget configuration; seeds initial UI visibility. */
  enabledByDefault: boolean;
  layer: Layer | undefined;
};

/**
 * One Deck.gl layer (or none while loading / empty GeoJSON) per static overlay row, with stable ids.
 */
export function useStaticMapOverlayEntries(
  overlays: StaticMapOverlaysOverlayConfiguration[],
): StaticMapOverlayEntry[] {
  const results = useQueries({
    queries: overlays.map((o) => ({
      queryKey: ["data", "staticMapOverlay", o.static_map_overlay_id] as const,
      queryFn: () => api.data.getStaticMapOverlay(o.static_map_overlay_id),
      select: (res: AxiosResponse<StaticMapOverlaysOverlay>) =>
        res.data.data as unknown as FeatureCollection,
      enabled: !!o.static_map_overlay_id,
    })),
  });

  /* `useQueries` often returns a new `results` array each render; depending on it directly
   * recomputes entries every time, retriggers sync effect + setState, → max update depth. */
  const resultsStableKey = results
    .map((r) => `${r.status}:${r.fetchStatus}:${r.dataUpdatedAt}`)
    .join("|");

  return useMemo(() => {
    return overlays.map((o, i) => {
      const instanceId = `${o.static_map_overlay_id}-${i}`;
      const geoJson = results[i]?.data;
      const rgb = getRgbForStoredOverlayColor(o.color);
      const layer =
        geoJson != null ? makeDeckLayerFromGeoJson(instanceId, geoJson, rgb) : undefined;
      return {
        instanceId,
        label: o.label?.trim() ? o.label : o.static_map_overlay_id,
        enabledByDefault: o.enabled_by_default,
        layer,
      };
    });
    /* `results` intentionally omitted: identity churns; resultsStableKey + overlays gate rebuilds. */
  }, [overlays, resultsStableKey]);
}

function makeDeckLayerFromGeoJson(
  overlayInstanceId: string,
  geoJson: FeatureCollection,
  color: ColorRGB,
): Layer | undefined {
  if (!geoJson.features?.length) return undefined;

  const first = geoJson.features[0]?.geometry;
  if (first?.type === "Point") {
    return new IconLayer({
      id: `static-map-overlay-icon-${overlayInstanceId}`,
      slot: "middle",
      data: geoJson.features,
      getPosition: (d) => [
        Number(d.geometry.coordinates[0]),
        Number(d.geometry.coordinates[1]),
      ],
      getIcon: () => "marker",
      getSize: () => 32,
      getColor: () => color,
      iconAtlas: MarkerIcon,
      iconMapping: {
        marker: {
          x: 0,
          y: 0,
          height: 64,
          width: 64,
          anchorX: 32,
          anchorY: 64,
          mask: true,
        },
      },
    });
  }

  return new GeoJsonLayer({
    id: `static-map-overlay-geojson-${overlayInstanceId}`,
    slot: "middle",
    data: geoJson,
    filled: false,
    extruded: false,
    stroked: true,
    lineWidthMinPixels: 2,
    getLineColor: () => color,
  });
}
